import { Component, OnInit } from '@angular/core';
import { ColumnMode, SelectionType, SortType } from '@swimlane/ngx-datatable';
import { DataService } from 'src/app/data.service';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, EMPTY, firstValueFrom, retry, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { kirBeolvas, kirDatum, KirOlvasas, nevBont, kretaCimResz } from '../kir-import';

@Component({
  selector: 'app-importszulok',
  templateUrl: './importszulok.component.html',
  styleUrls: ['./importszulok.component.scss'],
})
export class ImportszulokComponent implements OnInit {

  excelData: any[];
  protected readonly ColumnMode = ColumnMode;
  protected readonly SortType = SortType;
  protected readonly SelectionType = SelectionType;
  allomany
  tanulok
  overwrite = false
  generateKeycloakImport = false
  cdis = false;
  uploading = false;
  sajatIntezmeny

  /* ── KIR „Szülő, törvényes képviselő" sablon ───────────────────────────
   *
   *  A KIR gondviselő-exportja gyerekenként SOROKAT ad (egy gyerekhez több szülő),
   *  nem a belső, `username`-alapú alakot. A gondviselői felhasználónév a KIR
   *  szokása szerint a gyerek oktatási azonosítója + G01/G02/G03 – a fájl sorrendje
   *  szerint. A rendszerben eddig is ilyen azonosítók vannak, és a belépés is ezt
   *  ismeri fel.
   */
  kirOlvasas: KirOlvasas = null;
  kirSzuloSorok: any[] = null;      // null = sem KIR-, sem KRÉTA-formátumú fájl
  /*  Melyik gondviselő-exportot ismertük fel: 'kir' | 'kreta' | null.
   *
   *  A két rendszer ugyanazt az adatot adja, csak más oszlopnevekkel és más
   *  bontásban (a KRÉTA egyetlen mezőben adja a nevet, és négy címblokkot ír).
   *  A felismerés után a KRÉTA-sorokat a KIR alakjára fordítjuk, így a család-,
   *  felhasználónév- és előnézet-logika EGY helyen marad - két párhuzamos
   *  feldolgozásból előbb-utóbb két különböző eredmény lesz.
   */
  forras: 'kir' | 'kreta' | null = null;
  kirOsszegzes: any = null;
  /*  Előnézet a KIR-fájlhoz.
   *
   *  A lenti ngx-datatable a BELSŐ formátum oszlopneveire van kötve (username, vez,
   *  ker…), a KIR-fájlban viszont egészen más kulcsok vannak – ezért ott minden cella
   *  üresen maradt. A KIR-nek saját nézet kell, és épp azt kell mutatnia, amit
   *  feltöltés előtt ellenőrizni érdemes: melyik gondviselő MILYEN felhasználónevet kap.
   */
  kirElonezet: any[] = null;

  /** KIR rokonsági fok → a `rokonsag` tábla azonosítója. */
  private static readonly ROKONSAG: { [nev: string]: number } = {
    'édesapa': 1, 'édesanya': 1, 'nevelő apa': 1, 'nevelő anya': 1, 'gondnok': 1,
    'testvér': 5, 'nagyszülő': 6, 'gyám': 7,
    'egyéb rokoni kapcsolat': 8, 'egyéb nem rokoni kapcsolat': 9,
    'hivatásos nevelőszülő': 10, 'gyermekotthon vezetője': 11, 'kollégiumi nevelő': 12,
  };

  constructor(private http: HttpClient, private data: DataService) { }

  private normalizeUsername(value: any): string {
    return String(value ?? '').trim();
  }

  private szoveg(v: any): string { return String(v ?? '').trim(); }

  private rokonsagId(nev: any): number {
    const kulcs = this.szoveg(nev).toLowerCase();
    // Ismeretlen fok esetén „Szülő / Gondviselő": ez a legkevésbé félrevezető,
    // és a kapcsolat maga fontosabb, mint a pontos megnevezés.
    return ImportszulokComponent.ROKONSAG[kulcs] ?? 1;
  }

  /*  KRÉTA-sor → KIR-alak.
   *
   *  Amit a KRÉTA MÁSKÉNT ad:
   *   - a gondviselő neve EGYETLEN mezőben van (a szétbontás szabályát lásd a
   *     `nevBont`-nál, és az eredménye az előnézetben ellenőrizhető),
   *   - négy címblokkot ír (állandó / ideiglenes / tartózkodási / ismeretlen),
   *     miközben a `szulo` tábla kettőt tárol: állandó + tartózkodási. Az
   *     „ismeretlen" blokkot eldobjuk, a tartózkodási hiányában az ideiglenest
   *     használjuk – az az, ami a tartózkodási helynek megfelel,
   *   - az emelet/ajtó rovatot kitöltetlenül is teleírja kötőjelekkel (ezt a
   *     `kretaCimResz` takarítja),
   *   - NEM mondja meg, ki a törvényes képviselő. A rovatot ezért a rokonsági
   *     fokból következtetjük (szülő/nevelő/gyám igen, testvér és egyéb nem
   *     rokoni kapcsolat nem), és ez az előnézetben látszik is.
   */
  private kretaCimBlokk(r: any, elotag: string): any {
    const cim = [r[`${elotag} közterület neve`], r[`${elotag} közterület jellege`],
                 r[`${elotag} házszám`],
                 kretaCimResz(r[`${elotag} emelet`]), kretaCimResz(r[`${elotag} ajtó`])]
      .map((x) => this.szoveg(x)).filter(Boolean).join(' ');
    const irsz = Number(this.szoveg(r[`${elotag} irányítószám`]).replace(/\D/g, '')) || null;
    const telepules = this.szoveg(r[`${elotag} város`]) || null;
    // Az ORSZÁG önmagában nem cím: a KRÉTA a kitöltetlen blokkokba is beírja.
    if (!irsz && !telepules && !cim) return null;
    return { irsz, telepules, cim: cim || null };
  }

  /** A törvényes képviselő rovatot a KRÉTA nem adja – a rokonsági fokból következik. */
  private kretaTorvKepv(rokonsag: any): number {
    const k = this.szoveg(rokonsag).toLowerCase();
    return /testvér|egyéb nem rokoni/.test(k) ? 0 : 1;
  }

  private kretaSorokKirAlakra(sorok: any[]): any[] {
    return (sorok || []).map((r) => {
      const nev = nevBont(r['Szülő, törvényes képviselő neve']);
      const allando = this.kretaCimBlokk(r, 'Állandó lakcím');
      const tartozkodasi = this.kretaCimBlokk(r, 'Tartózkodási hely')
        || this.kretaCimBlokk(r, 'Ideiglenes lakcím');
      const rokonsag = this.szoveg(r['Rokonság foka']);
      return {
        'Tanuló oktatási azonosító': this.szoveg(r['Oktatási azonosító']),
        'Tanuló neve': [r['Tanuló vezetékneve'], r['Tanuló utóneve']]
          .map((x) => this.szoveg(x)).filter(Boolean).join(' '),
        'Rokonsági foka': rokonsag,
        'Szülő, törvényes képviselő előtagja': nev.elo,
        'Szülő, törvényes képviselő vezetékneve': nev.vez,
        'Szülő, törvényes képviselő keresztneve': nev.ker,
        'Törvényes képviselő': this.kretaTorvKepv(rokonsag) ? 'Igen' : 'Nem',
        'Szülő, törvényes képviselő értesítési e-mail cím': this.szoveg(r['Értesítési e-mail cím']),
        'Szülő, törvényes képviselő telefonszáma': this.szoveg(r['Értesítési telefonszám']),
        // A jelölő nem a címek meglétén múlik: cím nélküli gondviselőnél is a
        // KRÉTA-ág vigye tovább a sort, különben a KIR (más oszlopnevű) ága kapná meg.
        _kreta: true,
        _allando: allando,
        _tartozkodasi: tartozkodasi,
      };
    });
  }

  /** Egy KIR-sor lakcíme egy sorba – a `szulo` tábla egyetlen `cim` mezőt tart. */
  private kirCim(r: any): string {
    return [r['Közterület név'], r['Közterület jelleg'], r['Házszám'],
            this.szoveg(r['Emelet']) && !/^-+$/.test(this.szoveg(r['Emelet'])) ? r['Emelet'] : '',
            this.szoveg(r['Ajtó'])]
      .map((x) => this.szoveg(x)).filter(Boolean).join(' ');
  }

  /**
   *  KIR gondviselő-sorok → a belső küldemény (szülő, család, kapcsolat).
   *
   *  Gyerekenként csoportosít, és a fájl sorrendjében osztja ki a G01/G02/G03
   *  azonosítókat. Egy gyerekhez háromnál több gondviselő is jöhet – a régi,
   *  „G01 és a hozzá tartozó G02" logika a harmadikat elnyelte volna.
   */
  private kirKuldemeny() {
    const kuld: any = { szulo: [], csalad: [], tanulo_szulo: [] };
    const gyerekek = new Map<string, any[]>();
    for (const r of this.kirSzuloSorok || []) {
      const oktazon = this.szoveg(r['Tanuló oktatási azonosító']);
      if (!oktazon) continue;
      if (!gyerekek.has(oktazon)) gyerekek.set(oktazon, []);
      gyerekek.get(oktazon).push(r);
    }

    const ismeretlenGyerek: string[] = [];
    const elonezet: any[] = [];
    const letezoTanulok = new Set((this.tanulok || []).map((t: any) => String(t.oktazon)));
    for (const [oktazon, sorok] of gyerekek) {
      const ismertGyerek = letezoTanulok.has(oktazon);
      if (!ismertGyerek) ismeretlenGyerek.push(oktazon);
      const csalad = [oktazon];
      sorok.forEach((r, i) => {
        const username = `${oktazon}G${String(i + 1).padStart(2, '0')}`;
        // A KRÉTA-sor MINDKÉT címet hozza (állandó + tartózkodási); a KIR soronként
        // egyet ad, és a „Cím típusa" mondja meg, melyiket.
        const ketCim = !!r._kreta;
        const tartozkodas = !ketCim && /tartózkod|ideiglenes/i.test(this.szoveg(r['Cím típusa']));
        const cim = this.kirCim(r);
        const irsz = Number(this.szoveg(r['Irányítószám']).replace(/\D/g, '')) || null;
        kuld.szulo.push({
          elo: this.szoveg(r['Szülő, törvényes képviselő előtagja']) || null,
          vez: this.szoveg(r['Szülő, törvényes képviselő vezetékneve']),
          ker: this.szoveg(r['Szülő, törvényes képviselő keresztneve']),
          torv_kepv: /igen/i.test(this.szoveg(r['Törvényes képviselő'])) ? 1 : 0,
          szul_hely: null, szul_ido: null,
          username,
          email: this.szoveg(r['Szülő, törvényes képviselő értesítési e-mail cím']) || null,
          telefon: this.szoveg(r['Szülő, törvényes képviselő telefonszáma']) || null,
          irsz: ketCim ? (r._allando?.irsz ?? null) : (tartozkodas ? null : irsz),
          telepules: ketCim ? (r._allando?.telepules ?? null)
                            : (tartozkodas ? null : (this.szoveg(r['Helységnév']) || null)),
          cim: ketCim ? (r._allando?.cim ?? null) : (tartozkodas ? null : (cim || null)),
          tart_irsz: ketCim ? (r._tartozkodasi?.irsz ?? null) : (tartozkodas ? irsz : null),
          tart_telepules: ketCim ? (r._tartozkodasi?.telepules ?? null)
                                 : (tartozkodas ? (this.szoveg(r['Helységnév']) || null) : null),
          tart_cim: ketCim ? (r._tartozkodasi?.cim ?? null) : (tartozkodas ? (cim || null) : null),
          intezmeny: this.sajatIntezmeny.intezmenyId,
          ig_tipus: null, ig_szam: null,
          jogkor: '["gondviselo"]',
          anya_vez: null, anya_ker: null,
        });
        csalad.push(username);
        kuld.tanulo_szulo.push({ tanulo: oktazon, szulo: username, rokonsag: this.rokonsagId(r['Rokonsági foka']) });
        elonezet.push({
          gyerek: this.szoveg(r['Tanuló neve']), oktazon, ismertGyerek, username,
          nev: [r['Szülő, törvényes képviselő előtagja'], r['Szülő, törvényes képviselő vezetékneve'],
                r['Szülő, törvényes képviselő keresztneve']].map((x) => this.szoveg(x)).filter(Boolean).join(' '),
          rokonsag: this.szoveg(r['Rokonsági foka']),
          torv_kepv: /igen/i.test(this.szoveg(r['Törvényes képviselő'])),
          telefon: this.szoveg(r['Szülő, törvényes képviselő telefonszáma']),
          email: this.szoveg(r['Szülő, törvényes képviselő értesítési e-mail cím']),
          cim: ketCim
            ? [r._allando?.irsz, r._allando?.telepules, r._allando?.cim]
                .map((x) => this.szoveg(x)).filter(Boolean).join(' ')
            : [this.szoveg(r['Irányítószám']), this.szoveg(r['Helységnév']), cim].filter(Boolean).join(' '),
          tart_cim: ketCim
            ? [r._tartozkodasi?.irsz, r._tartozkodasi?.telepules, r._tartozkodasi?.cim]
                .map((x) => this.szoveg(x)).filter(Boolean).join(' ')
            : '',
        });
      });
      kuld.csalad.push({ users: JSON.stringify(csalad) });
    }

    this.kirElonezet = elonezet;
    this.kirOsszegzes = {
      forras: this.forras,
      gyerek: gyerekek.size,
      gondviselo: kuld.szulo.length,
      ismeretlenGyerek,
      tobbMintKetto: [...gyerekek.values()].filter((x) => x.length > 2).length,
      tartozkodasi: elonezet.filter((e) => e.tart_cim).length,
    };
    return kuld;
  }

  private getGuardianSuffix(username: any): string {
    return this.normalizeUsername(username).slice(-3).toUpperCase();
  }

  private getChildOktazonFromGuardian(username: any): string {
    return this.normalizeUsername(username).slice(0, -3);
  }

  private birthDatePassword(value: any): string {
    const text = this.normalizeUsername(value);
    if (!text) return "Zenit123";

    const formatted = moment(text, ["YYYY-MM-DD", "YYYY. MM. DD.", "YYYY.MM.DD.", "YYYY.MM.DD"], true);
    const parsed = formatted.isValid() ? formatted : moment(text);
    return parsed.isValid() ? parsed.format("YYYYMMDD") : "Zenit123";
  }

  private keycloakUsername(row: any, fallback: string): string {
    const email = this.normalizeUsername(row["email"]);
    return email || fallback;
  }

  private downloadKeycloakUsers(rows: any[], fileName: string, role: string) {
    const users = rows
      .map((row) => ({
        username: row.username,
        enabled: true,
        firstName: row.firstName,
        lastName: row.lastName,
        email: row.email || undefined,
        attributes: { jogkor: [role], locale: ["hu"] },
        credentials: [{ type: "password", value: row.password, temporary: true }],
        groups: [role],
        realmRoles: [role, "default-roles-master"],
      }))
      .filter((row) => row.username);

    const blob = new Blob([JSON.stringify({ users }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  async ngOnInit() {
    this.sajatIntezmeny = (await this.data.getFromStorage("sajatIntezmeny"))
    this.getTanulok();
  }

  getTanulok() {
    let headers = {
      intezmeny: this.sajatIntezmeny.intezmenyId
    }
    this.http
      .get(
        '/api/nyilvantartas/tanulo/' + this.sajatIntezmeny.intezmenyId,
        {
          headers: headers,
        }
      )
      .subscribe({
        next: (response) => {
          this.tanulok = response
        },
        error: (error) => {
          console.error('Error fetching data:', error);

        },
      });
  }

  private async ensureTanulokLoaded() {
    if (Array.isArray(this.tanulok)) return;

    this.tanulok = await firstValueFrom(
      this.http.get('/api/nyilvantartas/tanulo/' + this.sajatIntezmeny.intezmenyId, {
        headers: {
          intezmeny: this.sajatIntezmeny.intezmenyId,
        },
      })
    );
  }

  async uploadData() {
    this.uploading = true;
    this.data.showLoading("Állományok ellenőrzése és feltöltés...")
    await this.ensureTanulokLoaded();

    // KIR-formátum: külön út, mert ott gyerekenként több sor van, és a felhasználónevet
    // nekünk kell kiosztani (G01, G02, G03 – akár háromnál több gondviselő is lehet).
    if (this.kirSzuloSorok) { await this.kirFeltoltes(); return; }

    let kuld = [];
    kuld["szulo"] = [];
    kuld["csalad"] = [];
    kuld["tanulo_szulo"] = [];
    console.log(this.tanulok)
    for await (let t of this.excelData) {
      const username = this.normalizeUsername(t["username"]);
      if (this.getGuardianSuffix(username) == "G01") {
        let csalad = [];
        let tsz = [];
        const oktazon = this.getChildOktazonFromGuardian(username);
        let tanulok = this.tanulok.find(item => String(item.oktazon) == oktazon)
        let szulok = this.excelData.find(item =>
          this.getChildOktazonFromGuardian(item["username"]) == oktazon &&
          this.getGuardianSuffix(item["username"]) == "G02"
        )
        const szulo2Username = szulok?.username ? this.normalizeUsername(szulok.username) : "";
        //console.log(szulok)
        if (tanulok?.oktazon) csalad.push(tanulok.oktazon)
        if (tanulok?.oktazon) tsz.push({ tanulo: tanulok.oktazon, szulo: username, rokonsag: 1 })
        if (szulo2Username) csalad.push(szulo2Username)
        if (szulo2Username && tanulok?.oktazon) tsz.push({ tanulo: tanulok.oktazon, szulo: szulo2Username, rokonsag: 1 })
        csalad.push(username)
        let i = 2;
        while (t[`gyermek${i}`]) {
          if (t[`gyermek${i}`] != "") {
            csalad.push(t[`gyermek${i}`])
            tsz.push({ tanulo: t[`gyermek${i}`], szulo: username, rokonsag: 1 })
            if (szulo2Username && tanulok?.oktazon) tsz.push({ tanulo: t[`gyermek${i}`], szulo: szulo2Username, rokonsag: 1 })
          }
          i++;
        }
        for await (let s of tsz) { kuld["tanulo_szulo"].push(s) }
        kuld["csalad"].push({ users: JSON.stringify(csalad) })
      }
      let adid = username;
      kuld["szulo"].push({
        elo: t["elo"],
        vez: t["vez"],
        ker: t["ker"],
        torv_kepv: t["torv_kepv"],
        szul_hely: t["szul_hely"],
        szul_ido: t["szul_ido"],
        username: username,
        email: t["email"],
        telefon: t["telefon"],
        irsz: t["irsz"],
        telepules: t["telepules"],
        cim: t["cim"],
        tart_irsz: t["tart_irsz"],
        tart_telepules: t["tart_telepules"],
        tart_cim: t["tart_cim"],
        intezmeny: t["intezmeny"],
        ig_tipus: t["ig_tipus"],
        ig_szam: t["ig_szam"],
        jogkor: t["jogkor"],
        anya_vez: t["anya_vez"],
        anya_ker: t["anya_ker"]
      });

    };
    console.log(kuld)
    if (this.generateKeycloakImport) {
      this.downloadKeycloakUsers(
        kuld["szulo"].map((t) => ({
          username: this.keycloakUsername(t, t.username),
          firstName: t.ker,
          lastName: t.vez,
          email: this.normalizeUsername(t.email),
          password: this.birthDatePassword(t.szul_ido),
        })),
        'szulo-keycloak-users.json',
        'gondviselo'
      );
    }
    this.http.post("/api/import/szulo", [kuld["szulo"], kuld["csalad"], kuld["tanulo_szulo"]]).pipe(retry(3), catchError(e => {        // need to capture the 'e' here...
      this.handleError(e, this.data);   // pass to your function
      return EMPTY;          // and return empty
    })).subscribe({ next: d => {

      this.data.hideLoading()
      console.log(d)

      if (d.toString().indexOf("OK")) {
        this.data.sendToast("Sikeres importálás", "success")
        
      }
      else
        this.data.sendToast("Hiba történt a feldolgozás során!", "error")
    }, error: () => this.data.hideLoading() });




  }
  /** A KIR-formátumú gondviselő-fájl feltöltése. */
  private async kirFeltoltes() {
    const kuld = this.kirKuldemeny();
    if (!kuld.szulo.length) {
      this.data.hideLoading(); this.uploading = false;
      this.data.sendToast('Nincs feltölthető gondviselő a fájlban.', 'error');
      return;
    }
    if (this.kirOsszegzes.ismeretlenGyerek.length && !confirm(
      `${this.kirOsszegzes.ismeretlenGyerek.length} gyerek oktatási azonosítója nincs meg a `
      + 'rendszerben – az ő gondviselőik is létrejönnek, de a kapcsolat csak akkor lesz '
      + 'látható, ha a tanulót is importálod. Folytatod?')) {
      this.data.hideLoading(); this.uploading = false;
      return;
    }
    if (this.generateKeycloakImport) {
      this.downloadKeycloakUsers(
        kuld.szulo.map((sz: any) => ({
          username: this.keycloakUsername(sz, sz.username),
          firstName: sz.ker, lastName: sz.vez,
          email: this.normalizeUsername(sz.email),
          password: this.birthDatePassword(sz.szul_ido),
        })), 'gondviselo-keycloak-users.json', 'gondviselo');
    }
    try {
      const d: any = await firstValueFrom(
        this.http.post('/api/import/szulo', [kuld.szulo, kuld.csalad, kuld.tanulo_szulo]));
      if (d?.error) throw new Error(d.error?.sqlMessage || d.error?.message || d.error);
      // A szerver a kötésnél inserted/skipped-et ad vissza, nem affectedRows-t.
      this.data.sendToast(`${kuld.szulo.length} gondviselő mentve, `
        + `${d?.tanulo_szulo?.inserted ?? 0} kapcsolat létrehozva`
        + (d?.tanulo_szulo?.skipped ? `, ${d.tanulo_szulo.skipped} már megvolt` : '') + '.', 'success');
    } catch (e: any) {
      this.data.sendToast('Hiba a feldolgozás során: ' + (e?.error?.error || e?.message || e), 'error');
    } finally {
      this.data.hideLoading();
      this.uploading = false;
    }
  }

  handleError(error: HttpErrorResponse, data) {

    data.hideLoading()

    data.sendToast("Nem sikerült elküldeni a kérést!", "error")
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  onFileChange(event: any) {
    try {
      console.log(event)
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const workbook = XLSX.read(e.target.result, { type: 'binary', cellDates: true });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        /*  KIR „Szülő, törvényes képviselő" sablon vagy a belső (username-alapú) alak?
         *  A KIR-fájl gyerekenként több sort ad, és nincs benne felhasználónév – azt
         *  nekünk kell kiosztanunk. A felismerés a kulcsoszlopokon megy.
         */
        const matrix = XLSX.utils.sheet_to_json(worksheet,
          { header: 1, raw: false, defval: null, blankrows: true }) as any[][];
        this.kirOlvasas = kirBeolvas(matrix, ['Tanuló oktatási azonosító', 'Rokonsági foka']);
        this.forras = this.kirOlvasas.sorok.length ? 'kir' : null;

        //  KRÉTA „Szülők, törvényes képviselők elérhetőségei" export.
        //  Más oszlopnevek, egy mezőben a név, négy címblokk – a sorokat a KIR
        //  alakjára fordítjuk, hogy innentől ugyanaz az út vigye tovább.
        if (!this.forras) {
          const kreta = kirBeolvas(matrix,
            ['Oktatási azonosító', 'Rokonság foka', 'Szülő, törvényes képviselő neve']);
          if (kreta.sorok.length) {
            this.forras = 'kreta';
            this.kirOlvasas = { ...kreta, sorok: this.kretaSorokKirAlakra(kreta.sorok) };
          }
        }

        this.kirSzuloSorok = this.kirOlvasas?.sorok?.length ? this.kirOlvasas.sorok : null;
        this.kirOsszegzes = null;
        this.kirElonezet = null;
        this.excelData = this.kirSzuloSorok
          || XLSX.utils.sheet_to_json(worksheet, { raw: true, defval: null }) as any[];
        // Előnézet: mit fog csinálni a feltöltés (a kirKuldemeny csak számol, nem küld).
        if (this.kirSzuloSorok) { try { this.kirKuldemeny(); } catch (err) { } }
        //const oktazonSet = new Set(this.allomany.map(item => item.oktazon)); // Create a set of Oktazon values from array A
        //if (!this.overwrite)
        //  this.excelData = this.excelData.filter(item => !oktazonSet.has(item["username"]));
        //this.excelData = this.excelData.filter(item => item["username"] != "");
        if (!this.kirSzuloSorok) {
          this.excelData.forEach(t => {
            if (t["szul_ido"]) t["szul_ido"] = kirDatum(t["szul_ido"], moment)
          })
        }
      };
      reader.readAsBinaryString(file);
    } catch (e) {
      console.log(e)
    }
  }



  files: any[] = [];

  /**
   * on file drop handler
   */
  onFileDropped($event) {
    this.prepareFilesList($event);
  }

  /**
   * handle file from browsing
   */
  fileBrowseHandler(event) {

    const inputElement = event.target as HTMLInputElement;
    if (inputElement.files) {
      const files: any = inputElement.files;
      // Now you can handle the files as needed
      this.prepareFilesList(files, event);
    }

  }

  /**
   * Delete file from files list
   * @param index (File index)
   */
  deleteFile(index: number) {
    this.files = [];
    this.excelData = [];
    (<HTMLInputElement>document.getElementById("fileDropRef")).value = null;
    this.cdis = false;
    //this.files.splice(index, 1);
  }

  /**
   * Simulate the upload process
   */
  uploadFilesSimulator(index: number, event) {
    setTimeout(() => {
      if (index === this.files.length) {
        return;
      } else {
        const progressInterval = setInterval(() => {
          if (this.files[index].progress === 100) {
            this.onFileChange(event);
            clearInterval(progressInterval);
            this.uploadFilesSimulator(index + 1, event);
          } else {
            this.files[index].progress += 5;
          }
        }, 20);
      }
    }, 100);
  }

  /**
   * Convert Files list to normal array list
   * @param files (Files List)
   */
  async prepareFilesList(files: Array<any>, event = null) {
    //for (const item of files) {
    this.cdis = true;
    const item = files[0]
    item.progress = 0;
    if (item.name.indexOf(".xls") != -1) {
      this.files[0] = item;
      this.allomany = await firstValueFrom(this.data.getAllomany((await this.data.getFromStorage("sajatIntezmeny")).intezmenyId))
      this.uploadFilesSimulator(0, event);
    } else {
      alert("Csak Excel fájlt lehet feltölteni!");
      this.deleteFile(0)
    }

    //}

  }

  /**
   * format bytes
   * @param bytes (File size in bytes)
   * @param decimals (Decimals point)
   */
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }




}
